import { useCallback, useEffect, useState } from "react";
import { useUserSession } from "shared/contexts/UserContext";
import type { AllBudgetTransactionsResponse } from "features/budget/api/Budget";
import { getAllTransactionsForUser } from "features/budget/api/Budget";
import { responseCache } from "shared/money/responseCache";
import { apiGetSWR } from "utils/apiClient";
import { dedupeBudgetTransactionsById } from "features/budget/components/transactions/budget-transactions/personalTransactionUtils";
import type { BudgetTransaction } from "types/BudgetTransaction";
import type { GroupTransaction } from "types/GroupTransaction";
import type { UserEntry } from "types/Group";

function budgetTransactionsEndpoint(username: string): string {
    return `/budget/${username}/transactions`;
}

function readCachedBudget(username: string | null): AllBudgetTransactionsResponse | null {
    if (!username) return null;
    const raw = responseCache.get<unknown>(budgetTransactionsEndpoint(username));
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const o = raw as Partial<AllBudgetTransactionsResponse>;
    return {
        budgetTransactions: o.budgetTransactions ?? [],
        groupTransactions: o.groupTransactions ?? [],
        groupPeople: o.groupPeople ?? [],
    };
}

export interface UseBudgetDataReturn {
    groupTransactions: GroupTransaction[];
    groupPeople: UserEntry[];
    allBudgetTxs: BudgetTransaction[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    handleAddTransaction: (tx: BudgetTransaction) => void;
    handleUpdateTransaction: (tx: BudgetTransaction) => void;
    handleDeleteTransaction: (id: string) => void;
}

/**
 * Loads personal budget transactions plus recent group transactions/people for the Personal page.
 */
export function useBudgetData(): UseBudgetDataReturn {
    const { profile } = useUserSession();

    const [groupTransactions, setGroupTransactions] = useState<GroupTransaction[]>(
        () => readCachedBudget(profile.username)?.groupTransactions ?? [],
    );
    const [groupPeople, setGroupPeople] = useState<UserEntry[]>(
        () => readCachedBudget(profile.username)?.groupPeople ?? [],
    );
    const [allBudgetTxs, setAllBudgetTxs] = useState<BudgetTransaction[]>(() => {
        const c = readCachedBudget(profile.username);
        return dedupeBudgetTransactionsById(c?.budgetTransactions ?? []);
    });
    const [loading, setLoading] = useState(() => {
        if (!profile.username) return false;
        return readCachedBudget(profile.username) === null;
    });
    const [error, setError] = useState<string | null>(null);

    const loadBudgetData = useCallback(async (shouldIgnore?: () => boolean) => {
        if (!profile.username || !profile.token) {
            if (!shouldIgnore?.()) {
                setGroupTransactions([]);
                setGroupPeople([]);
                setAllBudgetTxs([]);
                setLoading(false);
            }
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const txOutput = await getAllTransactionsForUser(profile.username, profile.token);

            if (!shouldIgnore?.()) {
                setGroupTransactions(txOutput.groupTransactions ?? []);
                setGroupPeople(txOutput.groupPeople ?? []);
                setAllBudgetTxs(dedupeBudgetTransactionsById(txOutput.budgetTransactions ?? []));
            }
        } catch (err) {
            console.error("Failed to load transactions", err);
            if (!shouldIgnore?.()) {
                setError("loadError");
            }
        } finally {
            if (!shouldIgnore?.()) setLoading(false);
        }
    }, [profile.username, profile.token]);

    useEffect(() => {
        const u = profile.username;
        const token = profile.token;
        if (!u || !token) {
            setGroupTransactions([]);
            setGroupPeople([]);
            setAllBudgetTxs([]);
            setLoading(false);
            return;
        }

        const endpoint = budgetTransactionsEndpoint(u);
        setError(null);
        let cancelled = false;

        const { stale, done, cancel } = apiGetSWR<AllBudgetTransactionsResponse>(
            endpoint,
            (fresh) => {
                setGroupTransactions(fresh.groupTransactions ?? []);
                setGroupPeople(fresh.groupPeople ?? []);
                setAllBudgetTxs(dedupeBudgetTransactionsById(fresh.budgetTransactions ?? []));
                setError(null);
            },
            token,
            { isCurrent: () => !cancelled },
        );

        if (stale !== null && typeof stale === "object" && !Array.isArray(stale)) {
            setGroupTransactions(stale.groupTransactions ?? []);
            setGroupPeople(stale.groupPeople ?? []);
            setAllBudgetTxs(dedupeBudgetTransactionsById(stale.budgetTransactions ?? []));
            setLoading(false);
        } else {
            setLoading(true);
        }

        void done
            .then((fresh) => {
                if (
                    !fresh
                    || typeof fresh !== "object"
                    || Array.isArray(fresh)
                ) {
                    throw new Error("Invalid transactions response payload");
                }
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Failed to load transactions", err);
                setError("loadError");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            cancel();
        };
    }, [profile.username, profile.token]);

    const handleAddTransaction = useCallback((tx: BudgetTransaction) => {
        setAllBudgetTxs((prev) => {
            const id = tx._id;
            const rest = id ? prev.filter((t) => t._id !== id) : prev;
            return dedupeBudgetTransactionsById([tx, ...rest]);
        });
    }, []);

    const handleUpdateTransaction = useCallback(
        (updated: BudgetTransaction) =>
            setAllBudgetTxs((prev) =>
                dedupeBudgetTransactionsById(
                    prev.map((tx) => (tx._id === updated._id ? updated : tx)),
                ),
            ),
        [],
    );

    const handleDeleteTransaction = useCallback(
        (id: string) =>
            setAllBudgetTxs((prev) => prev.filter((tx) => tx._id !== id)),
        [],
    );

    return {
        groupTransactions,
        groupPeople,
        allBudgetTxs,
        loading,
        error,
        refetch: loadBudgetData,
        handleAddTransaction,
        handleUpdateTransaction,
        handleDeleteTransaction,
    };
}
